import React from "react";
import styled from "styled-components";
import { useAppSelector } from "../../../store/hooks";
import { useGetMyCardsQuery } from "../api/respectCardsApi";
import { useNowTicker } from "../hooks/useNowTicker";
import { BRICK, DECK_INK, DECK_PAPER, INK, MINT, SERIF } from "../hand/deck";
import { AOTD_HAND_ID, handFrom, playableNow } from "../hand/handModel";
import { atTime, windowClock } from "../hand/playWindowCopy";
import { useAotdCardPlay } from "./AotdCardPlayContext";

const Button = styled.button<{ $live: boolean }>`
  display: block;
  margin: 12px auto 4px;
  border: 1.5px solid ${(p) => (p.$live ? INK : DECK_INK)};
  border-radius: ${(p) => (p.$live ? "1px" : "7px")};
  box-shadow: ${(p) => (p.$live ? "3px 3px rgba(0, 0, 0, 0.6)" : "2px 2px rgba(47, 42, 34, 0.35)")};
  background: ${(p) => (p.$live ? MINT : DECK_PAPER)};
  padding: 4px 14px;
  font-family: ${SERIF};
  font-style: italic;
  font-weight: 600;
  font-size: 16px;
  line-height: 1.25;
  color: ${(p) => (p.$live ? INK : DECK_INK)};
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;

  &:active {
    transform: translate(2px, 2px);
    box-shadow: 1px 1px rgba(0, 0, 0, 0.5);
  }
`;

const Tail = styled.span`
  color: ${BRICK};
  margin-left: 6px;
`;

/**
 * The cards button on the AOTD waiting screens (before the unlock and while
 * the art is painted), for members who hold cards. Before the window: "your
 * N cards can be played at 07:50", a tap takes you down to the hand. While the
 * window is open: "play your N cards" (N = playable now), a tap takes you to
 * the hand and lifts the first card you can play.
 *
 * Reads the same `/my-cards` cache entry as the hand and the Play modal, so it
 * costs no extra request.
 */
export default function AotdCardsReadyButton() {
  // the page's own test for a signed-in member (ArtOfTheDayPage)
  const signedIn = useAppSelector((s) => s.auth.bootstrapStatus === "ready" && Boolean(s.auth.token));
  const { data } = useGetMyCardsQuery(undefined, { skip: !signedIn });
  const now = useNowTicker(1000);
  const { focusPlayableCard } = useAotdCardPlay();

  if (!signedIn || !data?.hand) return null;
  const entries = handFrom(data);
  if (entries.length === 0) return null;

  const clock = windowClock(data.playWindow, now);
  let label: string;
  if (clock.open) {
    const n = entries.filter((e) => playableNow(e, true)).length;
    if (n === 0) return null;
    label = n === 1 ? "play your card" : `play your ${n} cards`;
  } else if (Number.isFinite(clock.nextOpensAt) && clock.nextOpensAt > now) {
    const n = entries.length;
    label = `${n === 1 ? "your card" : `your ${n} cards`} can be played ${atTime(clock.nextOpensAt, now)}`;
  } else {
    return null;
  }

  const live = clock.open;
  const onClick = () => {
    const hand = document.getElementById(AOTD_HAND_ID);
    hand?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    if (live) focusPlayableCard();
  };

  return (
    <Button type="button" $live={live} onClick={onClick}>
      {label}
      <Tail aria-hidden="true">↓</Tail>
    </Button>
  );
}
